import {
  CellClickedEvent,
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import {
  Box,
  ConfirmationDialogBox,
  IconButton,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import {
  AddressBookModel,
  deleteAddress,
  getFilteredAddressBookData,
  setAddressBookStore,
} from '@store/customers/customerDetails/addressBookTab';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { dateFormatter } from '@store/loadboard';
import { dateComparator } from '@store/loadboard/utils';
import { closeModal, openModal } from '@store/modals';
import { DeleteIcon, PencilIcon, PlaylistCheckIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID } from '@views/order/constants';
import { createEffect, createSignal } from 'solid-js';

import AddOrEditAddressBook from './AddOrEditAddressBook';
import {
  AddressColumn,
  agGridCellStyle,
  DefaultOrigin,
} from './AddressBookTab';
import { addressBookGridStyle as classes } from './classes';
import ImportAddressBook from './ImportAddressBook';

type AddressBookGridProps = {
  onRowClick?: (rowData: AddressBookModel) => void;
  customerId: number;
  type: 'LTL' | 'Stops';
};

const [defaultOriginItem, setDefaultOriginItem] = createSignal<
  AddressBookModel[]
>([]);

export { defaultOriginItem };

const AddressBookGrid = (props: AddressBookGridProps) => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [selectedRow, setSelectedRow] = createSignal<AddressBookModel | null>(
    null,
  );
  const [edit, setEdit] = createSignal(false);
  const [isUploadInProgress, setIsUploadInProgress] = createSignal(false);
  const [triggerAddUpdateFn, setTriggerAddUpdateFn] = createSignal<
    'add' | 'update' | 'delete' | 'none'
  >('none');

  let previousId = props.customerId;
  createEffect(() => {
    if (previousId !== props.customerId) {
      previousId = props.customerId;
      gridRef()?.api.refreshServerSide();
    }
  });
  const addModalId = 'addOrEditAddressBook';
  const uploadModalId = 'importAddressBook';

  const [isDeleteLoading, setIsDeleteLoading] = createSignal(false);

  const openEditAddress = (address: AddressBookModel) => {
    setSelectedRow(address);
    setEdit(true);
    setAddressBookStore('addOrEditErrorMessage', '');
    openModal(addModalId);
  };

  const commonFilterParams = {
    suppressAndOrCondition: true,
    buttons: ['apply', 'reset'],
    filterOptions: ['startsWith'],
  };

  const baseColumnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 220,
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      floatingFilter: true,
      cellRenderer: (params: { data: AddressBookModel }) => {
        return <DefaultOrigin {...params.data} />;
      },
      autoHeight: true,
      cellStyle: agGridCellStyle,
      sort: 'asc',
    },
    {
      field: 'addressLine1',
      headerName: 'Address',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      cellRenderer: (params: { data: AddressBookModel }) => {
        return (
          <AddressColumn
            val1={params.data.addressLine1}
            val2={params.data.addressLine2}
          />
        );
      },
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'city',
      headerName: 'City',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'state',
      headerName: 'State',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      maxWidth: 100,
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      cellRenderer: (params: { data: AddressBookModel }) => {
        return (
          <AddressColumn
            val1={params.data.contactName}
            val2={params.data.contactPhone}
            val3={params.data.contactEmail}
          />
        );
      },
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    ...(isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)()
      ? [
          {
            field: 'appointmentType',
            headerName: 'Appointment Type',
            filter: 'agTextColumnFilter',
            filterParams: commonFilterParams,
          },
          {
            field: 'schedulingMethod',
            headerName: 'Scheduling Method',
            filter: 'agTextColumnFilter',
            filterParams: commonFilterParams,
          },
        ]
      : []),
  ];

  const ltlColumnDefs: ColDef[] = [
    ...baseColumnDefs,
    {
      field: 'zip',
      headerName: 'Zip',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'internalNote',
      headerName: 'Internal Notes',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      cellStyle: agGridCellStyle,
      floatingFilter: false,
    },
    {
      field: 'createdOn',
      headerName: 'Created',
      filter: 'agDateColumnFilter',
      valueFormatter: dateFormatter,
      filterParams: {
        comparator: dateComparator,
        buttons: ['apply', 'reset'],
        suppressAndOrCondition: true,
        filterOptions: ['startsWith'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
  ];

  const stopsColumnDefs: ColDef[] = [
    ...baseColumnDefs,
    {
      field: 'createdOn',
      headerName: 'Created',
      filter: 'agDateColumnFilter',
      valueFormatter: dateFormatter,
      filterParams: {
        comparator: dateComparator,
        buttons: ['apply', 'reset'],
        suppressAndOrCondition: true,
        filterOptions: ['startsWith'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'specialInstructions',
      headerName: 'Location Instructions',
      autoHeight: true,
      cellStyle: agGridCellStyle,
      floatingFilter: false,
    },
    {
      field: 'actions',
      filter: false,
      sortable: false,
      cellRenderer: (params: { data: AddressBookModel }) => {
        return (
          <div
            class="flex items-center"
            data-address-book-row-action=""
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <IconButton
              onClick={() => {
                openEditAddress(params.data);
              }}
              title="Edit address book entry"
              dataTestId="address-book-grid-edit-icon-button"
            >
              <PencilIcon class="text-primary" />
            </IconButton>
            <IconButton
              onClick={() => {
                setSelectedRow(params.data);
                openDialogBox(DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID);
              }}
              disabled={isDeleteLoading()}
              title="Delete address book entry"
              dataTestId="address-book-grid-delete-icon-button"
            >
              <DeleteIcon class="text-red-700" />
            </IconButton>
          </div>
        );
      },
      pinned: 'right',
      maxWidth: 120,
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: AddressBookModel[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          name: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          contactName: '',
          country: '',
          email: '',
          phone: '',
          state: '',
          zip: '',
          createdOn: '',
          specialInstructions: '',
          internalNote: '',
          orderBy,
          sortBy,
          ...Object.fromEntries(
            Object.entries(params.request.filterModel).map(([key, value]) => [
              key,
              key === 'createdOn' ? value.dateFrom : value.filter,
            ]),
          ),
          customerId: props.customerId,
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 50,
        };

        try {
          gridRef()?.api.showLoadingOverlay();
          const data = await getFilteredAddressBookData(filteredObject);
          setDefaultOriginItem(data!.addresses);
          params.success({
            rowData: data!.addresses,
            rowCount: data!.totalCount,
          });
        } catch (error) {
          params.fail();
        } finally {
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  const handleDeleteRow = async () => {
    setIsDeleteLoading(true);
    const id = selectedRow()?.id;
    if (Boolean(id)) {
      const res = await deleteAddress(id!);
      if (Boolean(res)) {
        setTriggerAddUpdateFn('delete');
      }
    }
    setIsDeleteLoading(false);
  };

  const onAddAddressModalClose = () => {
    setEdit(false);
    setSelectedRow(null);
    closeModal(addModalId);
  };

  const updateRowAtIndex = () => {
    const rowNode = gridRef()?.api.getRowNode(selectedRow()?.id as string);
    if (rowNode) {
      rowNode.setData(selectedRow() as AddressBookModel);
      gridRef()?.api.refreshCells({ rowNodes: [rowNode] });
      if (gridRef()) {
        onGridReady(gridRef() as unknown as GridReadyEvent);
      }
    }
  };

  function addRow() {
    gridRef()?.api.applyServerSideTransaction({ add: [selectedRow()] });
    if (gridRef()) {
      onGridReady(gridRef() as unknown as GridReadyEvent);
    }
  }

  function removeRow() {
    const rowNode = gridRef()?.api.getRowNode(selectedRow()?.id as string);
    if (rowNode) {
      setDefaultOriginItem((prev) =>
        prev.filter((item) => item.id !== selectedRow()?.id),
      );
      gridRef()?.api.applyServerSideTransaction({ remove: [rowNode.data] });
    }
  }

  createEffect(() => {
    const triggerAction = triggerAddUpdateFn();
    if (triggerAction === 'update' && edit()) {
      updateRowAtIndex();
    } else if (triggerAction === 'add' && !edit()) {
      addRow();
    } else if (triggerAction === 'delete') {
      removeRow();
    }
    setTriggerAddUpdateFn('none');
  });

  return (
    <Stack class={classes.stackStyle}>
      <Box class={classes.addBtnBox}>
        <Button
          label={<div class={classes.addBtn}>Add Address</div>}
          variant="contained"
          size="sm"
          onClick={() => {
            setEdit(false);
            setAddressBookStore('addOrEditErrorMessage', '');
            setAddressBookStore('selectedAddress', null);
            openModal(addModalId);
          }}
          dataTestId={formatDataTestId('Add Address', 'button')}
        />
        <Button
          class="ml-4"
          label={<div class={classes.addBtn}>Import</div>}
          variant="contained"
          size="sm"
          onClick={() => {
            setAddressBookStore('addOrEditErrorMessage', '');
            openModal(uploadModalId);
          }}
          dataTestId="address-book-grid-add-edit-modal-btn"
        />
        <Box class="ml-4 flex gap-2 justify-between items-center">
          <Typography class="text-xs! font-semibold!">Legend</Typography>
          <PlaylistCheckIcon class="text-[#15668f]" />
          <Typography class="text-xs!">Default Origin</Typography>
        </Box>
      </Box>
      <Box class={classes.gridStyle}>
        <AgGridSolid
          ref={gridRef}
          columnDefs={props.type === 'LTL' ? ltlColumnDefs : stopsColumnDefs}
          overlayNoRowsTemplate="<span class='ag-overlay-loading-center'>Failed to fetch data from API</span>"
          gridOptions={{
            defaultColDef: {
              flex: 1,
              filter: false,
              floatingFilter: true,
              sortable: true,
            },
            onGridReady: onGridReady,
            paginationPageSizeSelector: false,
            rowHeight: 40,
            rowModelType: 'serverSide',
            getRowId: (params: { data: AddressBookModel }) =>
              params.data.id as string,
            pagination: true,
            paginationPageSize: 50,
            cacheBlockSize: 50,
            sideBar: {
              toolPanels: [
                {
                  id: 'columns',
                  labelDefault: 'Columns',
                  labelKey: 'columns',
                  iconKey: 'columns',
                  toolPanel: 'agColumnsToolPanel',
                },
                {
                  id: 'filters',
                  labelDefault: 'Filters',
                  labelKey: 'filters',
                  iconKey: 'filter',
                  toolPanel: 'agFiltersToolPanel',
                },
              ],
              defaultToolPanel: '',
            },
          }}
          onCellClicked={(e: CellClickedEvent) => {
            if (props.onRowClick) return;
            if (
              e.data != null &&
              e.colDef.field !== 'delete' &&
              e.colDef.field !== 'actions'
            ) {
              setSelectedRow(e.data as AddressBookModel);
              setEdit(true);
              setAddressBookStore('addOrEditErrorMessage', '');
              openModal(addModalId);
            }
          }}
          onRowClicked={(params: {
            data: AddressBookModel;
            event?: Event | null;
          }) => {
            const target = params.event?.target;
            if (
              target instanceof Element &&
              target.closest('[data-address-book-row-action]')
            ) {
              return;
            }
            props.onRowClick && props.onRowClick(params.data);
          }}
          pagination
        />
      </Box>
      <BasicModal
        id={addModalId}
        title="Add/Edit Address"
        width="1100px"
        class="w-[1100px]! max-w-[calc(100vw-32px)]!"
        footer={false}
        onClose={onAddAddressModalClose}
      >
        <AddOrEditAddressBook
          gridRef={gridRef}
          customerId={props.customerId}
          edit={edit}
          setEdit={setEdit}
          selectedRow={selectedRow}
          handleClose={onAddAddressModalClose}
          setSelectedRow={setSelectedRow}
          triggerAddUpdateFn={triggerAddUpdateFn}
          setTriggerAddUpdateFn={setTriggerAddUpdateFn}
        />
      </BasicModal>
      <BasicModal
        id={uploadModalId}
        title="Upload Address Book"
        width="800px"
        footer={false}
        onClose={() => {
          if (!isUploadInProgress()) {
            closeModal(uploadModalId);
          }
        }}
      >
        <ImportAddressBook
          customerId={props.customerId}
          onUploadComplete={() => {
            gridRef()?.api.refreshServerSide();
          }}
          setIsUploading={setIsUploadInProgress}
        />
      </BasicModal>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID,
        )}
        closeDialogBox={(_reason) =>
          closeDialogBox(DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID)
        }
        dataTestId={DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID}
        id={DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID}
        title={`Are you sure you want to delete this following address/stop: ${
          selectedRow()?.name
        } ?`}
        onSubmit={async () => {
          await handleDeleteRow();
        }}
        onSubmitText="Delete"
      />
    </Stack>
  );
};

export default AddressBookGrid;
